import React from "react"
import { ThemeProvider, createTheme } from "@material-ui/core/styles"
import { CssBaseline } from "@material-ui/core"
import PropTypes from "prop-types"
import { withStyles } from "@material-ui/styles"
import { TIMESHEET_STATUS } from "./data"

const palette = {
  background: {
    default: "white",
    splitLeftPane: "#fafcff",
    shaded: "#fafcff",
  },
  primary: {
    main: "#2092ff",
    shaded: {
      background: "#fafcff",
    },
    disabled: "#7f7f7f",
  },
  secondary: {
    main: "#f63b3b",
  },
  shaded: {
    main: "#fafafa",
    border: "#cccccc",
    filter: "#fafcff",
    icon: "#bdbdbd",
    background: "#fafcff",
    darker: "#cccccc",
  },
  textDivider: {
    background: "#f8f8f8",
  },
  incomplete: {
    main: "#cccccc",
    background: "#fafcff",
  },
  // late: {
  //   main: "#ffc400",
  // },
  success: {
    main: "#18ce99",
    mainRgb: "24,206,153",
  },
  warning: {
    main: "#f63b3b",
    light: "#ff7961",
    dark: "#bb0013",
    background: "#f7dddd",
  },
  skipped: {
    checkbox: "#b9b9b9",
  },
  raised: {
    main: "#fd9317",
  },
  unconfirmed: {
    main: "#f63b3b",
  },
  priority: {
    main: "#fd9317",
  },
  invited: {
    main: "#fd9317",
  },
  active: {
    main: "#18ce99",
  },
  inactive: {
    main: "#bdbdbd",
  },
  scheduled: {
    main: "#fd9317",
  },
  featured: {
    main: "#ffcc00",
  },
  sensor: {
    temperature: "#42a5f5",
    humidity: "#fd9317",
  },
  body: {
    main: "#ed1c24",
  },
  inlineEditable: {
    hover: {
      background: "rgba(150,150,150,0.1)",
    },
  },
  punchClock: {
    [TIMESHEET_STATUS.SCHEDULED]: {
      backgroundColor: "#f5fffc",
    },
    [TIMESHEET_STATUS.NOT_SCHEDULED]: {
      backgroundColor: "#f5fffc",
    },
    [TIMESHEET_STATUS.ACTIVE]: {
      backgroundColor: "#ffefdc",
    },
    [TIMESHEET_STATUS.PAUSED]: {
      backgroundColor: "#fafcff",
    },
    [TIMESHEET_STATUS.ENDED]: {
      backgroundColor: "#fafcff",
    },
  },
  calendar: {
    complete: {
      borderColor: "#18ce99",
      backgroundColor: "#f5fffc",
    },
    future: {
      borderColor: "#cccccc",
      backgroundColor: "#fff",
      opacity: 0.5,
    },
    overdue: {
      borderColor: "#f63b3b",
      backgroundColor: "#f7dddd",
    },
    uncomplete: {
      borderColor: "#cccccc",
      backgroundColor: "#fff",
    },
  },
  avatar: {
    circle: {
      background: "#bdbdbd",
      color: "#1a1b43",
    },
    number: {
      background: "#fafafa",
      color: "#1a1b43",
    },
    plus: {
      background: "#1a1b43",
    },
  },
  chips: {
    background: "#efefef",
  },
  kb: {
    heading: "#1a1b43",
    search: {
      background: "#fafcff",
    },
    searchInput: {
      background: "#e9f7fe",
    },
  },
  hoverCard: {
    boxShadow:
      "0px 10px 10px -2px rgba(32, 146, 255, 0.15), 0px 3px 4px 0px rgba(0, 0, 0, 0.05), 0px 1px 8px 0px rgba(32, 146, 255,0.1)",
    transition: "all 0.3s",
  },
  images: {
    tile: {
      background: "#fafcff",
    },
    more: {
      background: "#d5d5d5",
    },
    backing: {
      background: "#f4f6f9",
    },
  },
  lists: {
    itemHover: "#fafcff",
  },
  buttons: {
    pickerAction: {
      text: "#2092ff",
    },
    light: {
      background: "#f3f3f3",
    },
  },
  textinput: {
    readonly: "#f8f8f8",
  },
  icons: {
    background: "#efefef",
  },
  links: {
    disabled: "#7f7f7f",
  },
  text: {
    light: "#7f7f7f",
    medium: "#6f6f6f",
    faint: "#e0e0e0",
    main: "#000000",
    tinted: "#1a1b43",
    white: "#ffffff",
    disabled: "rgba(0, 0, 0, 0.15)",
    readonly: "rgba(0, 0, 0, 0.75)",
  },
  mentions: {
    mention: "#2092ff",
  },
  progress: {
    background: "rgba(39, 158, 220, 0.15)",
    bar: {
      started: "#2092ff",
      complete: "#18ce99",
      overdue: "#f63b3b",
    },
  },
  step: {
    button: {
      background: {
        incomplete: "#dfefff",
        complete: "#def1fa",
      },
    },
  },
  stepper: {
    incomplete: "#f3f3f3",
  },
  chart: {
    background: "#def1fa",
  },
  cards: {
    accessNote: "#b9b9b9",
    switch: {
      backgroundColor: "#efefef",
    },
    selected: {
      borderColor: "#b9b9b9",
    },
    completed: {
      borderColor: "#18ce99",
      backgroundColor: "#f5fffc",
    },
    overdue: {
      borderColor: "#f63b3b",
    },
    skipped: {
      borderColor: "#b9b9b9",
      backgroundColor: "#f3f3f3",
    },
    divide: {
      normal: "#efefef",
      darker: "#e0e0e0",
    },
    icons: {
      incomplete: "#707574",
      complete: "#18ce99",
    },
  },
  quote: {
    background: "#f3f3f3",
  },
  notes: {
    main: {
      backgroundColor: "rgba(39, 158, 220, 0.15)",
      color: "#2092ff",
    },
    important: {
      backgroundColor: "rgba(253, 147, 23, 0.15)",
      color: "rgb(253, 147, 23)",
    },
    danger: {
      backgroundColor: "rgba(246, 59, 59, 0.15)",
      color: "#f63b3b",
    },
  },
}

const muiTheme = createTheme({
  palette: { ...palette },
  dimensions: {
    header: {
      height: 60,
    },
    punchClock: {
      height: 68,
    },
    offlineBanner: {
      height: 40,
    },
    dialogs: {
      alerts: {
        width: 327,
      },
      pickers: {
        width: 327,
        wideWidth: null,
      },
      training: {
        width: 327,
      },
    },
    footer: {
      mobile: {
        paddingTop: 15,
        height: 100,
      },
    },
  },
  typography: {
    fontFamily: [
      "Inter",
      "Roboto",
      "'\"Helvetica Neue\"'",
      "Arial",
      "sans-serif",
      "'\"Apple Color Emoji\"'",
      "'\"Segoe UI Emoji\"'",
      "'\"Segoe UI Symbol\"'",
      "'\"Noto Emoji\"'",
      "'\"Noto Color Emoji\"'",
    ].join(","),
  },
  overrides: {
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: "#d2d2d2",
      },
    },
    MuiButton: {
      root: {
        textTransform: "none",
      },
      text: {
        textTransform: "none",
        fontWeight: "600",
        fontSize: 14,
        color: palette.primary.main,
      },
      contained: {
        textTransform: "none",
        fontWeight: "600",
        paddingLeft: "20px",
        paddingRight: "20px",
        boxShadow: "none",
      },
      containedPrimary: {
        color: "white",
        textTransform: "none",
        transition: "all 0.3s",
        "&:hover": {
          boxShadow: "0px 0px 10px 3px rgba(32, 146, 255, 0.4)",
          "background-color": "#2e99ff",
          transition: "all 0.3s ease",
        },
        "&:focus": {
          boxShadow: "0px 0px 10px 3px rgba(32, 146, 255, 0.4)",
          "background-color": "#2e99ff",
          transition: "all 0.3s ease",
        },
      },
    },
    MuiButtonGroup: {
      contained: {
        boxShadow: "none",
      },
    },
    MuiIconButton: {
      root: {
        color: "black",
        // "&:hover": {
        //   "background-color": "transparent",
        // },
      },
    },
    MuiLink: {
      button: {
        fontSize: 14,
        fontWeight: "600",
      },
    },
    MuiBreadcrumbs: {
      li: {
        fontSize: 14,
        lineHeight: "16px",
        padding: "4px",
        color: palette.text.main,
        "&& a": {
          color: palette.primary.main,
          fontWeight: "600",
        },
        "&:hover": {
          background: "#ecf4ff",
          borderRadius: "4px",
        },
        "&:focus": {
          background: "#ecf4ff",
          borderRadius: "4px",
        },
        "&:last-child": {
          background: "none",
        },
      },
    },
    MuiDialog: {
      paperFullScreen: {
        marginTop: 60,
        paddingBottom: 240,
      },
      paper: {
        position: "fixed",
        top: 0,
      },
      paperWidthMd: {
        maxWidth: "768px",
      },
    },
    MuiDialogTitle: {
      root: {
        paddingTop: 20,
        "&& h2": {
          fontSize: 20,
          fontWeight: "600 !important",
          lineHeight: "28px",
        },
      },
    },
    MuiDialogActions: {
      root: {
        fontSize: 14,
        fontWeight: "600",
      },
    },
    MuiBottomNavigationAction: {
      label: {
        paddingTop: 5,
        fontSize: 11,
        color: "black",
        "&$selected": {
          color: "black",
          fontSize: 11,
          background: "none !important",
        },
      },
    },
    MuiTab: {
      wrapper: {
        textTransform: "none",
        color: "black",
        fontSize: 14,
        fontWeight: "600",
        lineHeight: "16px",
      },
    },
    MuiTabs: {
      indicator: {
        background: "linear-gradient(to right, #f63b3b, #fd9317) left bottom no-repeat !important",
        height: 3,
      },
    },

    MuiAccordion: {
      root: {
        marginBottom: 0,
        "&:before": {
          display: "none",
        },
        "&$expanded": {
          height: "auto",
          minHeight: "auto",
          marginTop: 0,
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        border: "none",
        paddingLeft: 0,
        paddingRight: 0,
        margin: 0,
        padding: 0,
      },
    },
    MuiAccordionDetails: {
      root: {
        border: "none",
        paddingLeft: 0,
        paddingRight: 0,
      },
    },

    MuiPaper: {
      rounded: {
        borderRadius: "8px",
      },
      elevation2: {
        boxShadow:
          "0px 3px 1px -2px rgba(32, 146, 255, 0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(32, 146, 255, 0.12)",
      },
      elevation3: {
        boxShadow:
          "0px 3px 3px -2px rgba(32, 146, 255, 0.15), 0px 3px 4px 0px rgba(0, 0, 0, 0.05), 0px 1px 8px 0px rgba(32, 146, 255,0.1)",
      },
      elevation8: {
        boxShadow:
          "0px 5px 5px -3px rgba(32, 146, 255, 0.15), 0px 8px 10px 1px rgba(0, 0, 0, 0.05), 0px 3px 14px 2px rgba(32, 146, 255,0.1)",
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: "rgba(8, 8, 33, 0.6)",
      },
    },
    MuiPickersToolbarText: {
      // toolbarBtnSelected: {
      //   color: "rgba(255, 255, 255, 1)"
      // },
      // toolbarTxt: {
      //   color: "rgba(255, 255, 255, 1)"
      // }
    },
    MuiListItem: {
      gutters: {
        paddingTop: "8px",
        paddingBottom: "8px",
      },
    },
    MuiAvatar: {
      root: {
        fontSize: "0.9rem",
        fontWeight: "600",
      },
    },
  },
  props: {
    MuiButton: {
      disableElevation: true,
    },
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiCheckbox: {
      disableRipple: true,
    },
  },
})

// cssbaseline styles override
const styles = (theme) => ({
  "@global": {
    html: {},
    body: {
      height: "100%",
      color: "#1a1b43",
    },
    "#root": {
      height: "100%",
      minHeight: "100vh",
      display: "flex",
      flexFlow: "column",
      flexGrow: 1,
    },
    a: {
      textDecoration: "none",
    },
    "@keyframes successGlow": {
      "0%": {
        boxShadow: `0 0 5px rgba(${theme.palette.success.mainRgb},.2), inset 0 0 5px rgba(${theme.palette.success.mainRgb},.1), 0 2px 0 rgba(${theme.palette.success.mainRgb},.12)`,
      },
      "70%": {
        boxShadow: `0 0 20px rgba(${theme.palette.success.mainRgb},.9), inset 0 0 10px rgba(${theme.palette.success.mainRgb},.6), 0 2px 0 rgba(${theme.palette.success.mainRgb},.12)`,
      },
      "100%": {
        boxShadow: `0 0 5px rgba(${theme.palette.success.mainRgb},.2), inset 0 0 5px rgba(${theme.palette.success.mainRgb},.1), 0 2px 0 rgba(${theme.palette.success.mainRgb},.12)`,
      },
    },
    "@keyframes slide": {
      "0%": {
        transform: "translateX(-100%)",
      },
      "100%": {
        transform: "translateX(100%)",
      },
    },
    "@keyframes fadeInOpacity": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
    "@keyframes fadeInOpacityLight": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 0.3,
      },
    },
    "@media (max-width: 1024px)": {
      "#hubspot-messages-iframe-container": {
        display: "none",
        visibility: "hidden",
      },
    },
    ".kiosk #hubspot-messages-iframe-container": {
      display: "none",
      visibility: "hidden",
    },
    ".device #hubspot-messages-iframe-container": {
      display: "none",
      visibility: "hidden",
    },
    ".BeaconContainer": {
      zIndex: "999999 !important",
    },
    ".announcekit-frame-wrapper": {
      width: "90vw !important",
      maxWidth: "750px !important",
    },
  },
})

// eslint-disable-next-line no-unused-vars
const OverrideCssBaseline = ({ classes }) => null
OverrideCssBaseline.propTypes = {
  classes: PropTypes.object.isRequired,
}

const WithStylesOverrideCssBaseline = withStyles(styles)(OverrideCssBaseline)

const Theme = ({ children }) => (
  <ThemeProvider theme={muiTheme}>
    <CssBaseline />
    <WithStylesOverrideCssBaseline />
    {children}
  </ThemeProvider>
)

export default Theme
